import { Button, Spinner, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { Document, DocumentType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { getMissingDocumentTypes } from '@pelicargo/utils';
import { useMemo } from 'react';

import { useDocuments } from '../../hooks/documents/useDocuments';
import { useShipmentForms } from '../../hooks/shipmentForm/useShipmentForms';
import { buildAwbDocumentLabels } from '../../utils/awbDocumentLabels';
import { DocumentRow } from './DocumentRow';
import { DocumentsModal } from './DocumentsModal';

type Props = {
  heading?: string;
  documentTypes?: DocumentType[];
  requestId?: number;
  accountId?: number;
};

export const DocumentsForm = ({
  documentTypes: defaultDocumentTypes = [],
  requestId,
  accountId,
  heading = 'Documents',
}: Props) => {
  const disclosure = useDisclosure();
  const [documents, isLoading] = useDocuments(requestId, accountId);
  const { data: shipmentForms } = useShipmentForms(requestId);
  const awbLabelByDocumentId = useMemo(() => buildAwbDocumentLabels(shipmentForms), [shipmentForms]);

  const cleanedDocuments = useMemo(() => {
    const missingDocumentTypes = getMissingDocumentTypes(documents, defaultDocumentTypes);

    // Include all current documents in the list. If a document type is not in the list, it will be added.
    const finalDocuments = [
      ...documents,
      ...missingDocumentTypes.map((type) => ({ type }) as Partial<Document>),
    ].filter((doc) => doc?.type || doc?.id);

    return finalDocuments;
  }, [defaultDocumentTypes, documents]);

  const renderDocumentRow = (document: Partial<Document>) => (
    <DocumentRow
      key={document.id}
      document={document}
      awbLabel={document.id ? awbLabelByDocumentId[document.id] : undefined}
    />
  );

  if (isLoading) return <Spinner />;

  return (
    <>
      <Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text fontSize="md" fontWeight="bold">
            {heading}
          </Text>
          <Button variant="ghost" colorScheme="primary" size="xs" onClick={disclosure.onOpen}>
            Manage
          </Button>
        </Stack>
        <Stack>{cleanedDocuments.map(renderDocumentRow)}</Stack>
        <Show if={cleanedDocuments.length === 0}>
          <Text fontSize="sm" color="gray.500">
            No documents uploaded.
          </Text>
        </Show>
      </Stack>
      <DocumentsModal
        requestId={requestId}
        accountId={accountId}
        isOpen={disclosure.isOpen}
        onClose={disclosure.onClose}
        documentTypes={defaultDocumentTypes}
        defaultDocuments={documents}
      />
    </>
  );
};
